import { useState, useEffect, useMemo, useRef, Fragment } from 'react'
import { Users, Calendar, IndianRupee, Bike, Bus, MapPin, Route, ChevronDown, ChevronUp } from 'lucide-react'
import api from '../../Includes/api/api'
import { loadGoogleMaps } from 'Includes/googleMaps'

const formatMoney = (value) =>
  `₹${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`

const formatKm = (value) => `${Number(value || 0).toLocaleString('en-IN', { maximumFractionDigits: 1 })} km`

const schoolName = (areaName) => (areaName || '').split(',')[0].trim()

const localToday = () => {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

const tracePoints = (points) => {
  const ordered = [...(points || [])]
    .map((point) => ({
      lat: Number(point.latitude),
      lng: Number(point.longitude),
      timestamp: point.timestamp || '',
    }))
    .filter((point) => (
      Number.isFinite(point.lat) && Number.isFinite(point.lng)
      && Math.abs(point.lat) <= 90 && Math.abs(point.lng) <= 180
    ))
    .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
  const unique = []
  ordered.forEach((point) => {
    const previous = unique[unique.length - 1]
    if (previous && Math.abs(previous.lat - point.lat) < 0.00002 && Math.abs(previous.lng - point.lng) < 0.00002) return
    unique.push(point)
  })
  return unique
}

const drawTrace = (maps, map, bounds, points, color, endTitle, markEnds = true) => {
  if (!points.length) return
  points.forEach((point) => bounds.extend(point))
  if (points.length >= 2) {
    new maps.Polyline({
      map,
      path: points,
      strokeColor: color,
      strokeWeight: 5,
      strokeOpacity: 0.95,
      geodesic: true,
      icons: [{
        icon: { path: maps.SymbolPath.FORWARD_OPEN_ARROW, scale: 1.5, strokeColor: color },
        offset: '32px',
        repeat: '90px',
      }],
    })
  }
  if (!markEnds) return
  new maps.Marker({ map, position: points[0], title: 'Start', label: 'S' })
  new maps.Marker({
    map,
    position: points[points.length - 1],
    title: endTitle,
    label: endTitle === 'Home' ? 'H' : 'E',
  })
}

function PathMap({ toSchool = [], rideHome = [], height = 320 }) {
  const mapRef = useRef(null)
  const containerRef = useRef(null)
  const toSchoolTrace = useMemo(() => tracePoints(toSchool), [toSchool])
  const rideHomeTrace = useMemo(() => tracePoints(rideHome), [rideHome])
  const traceKey = useMemo(
    () => JSON.stringify({ toSchool: toSchoolTrace, rideHome: rideHomeTrace }),
    [toSchoolTrace, rideHomeTrace],
  )

  useEffect(() => {
    if (!containerRef.current) return undefined
    if (toSchoolTrace.length + rideHomeTrace.length === 0) return undefined
    let cancelled = false
    loadGoogleMaps().then((maps) => {
      if (cancelled || !containerRef.current) return
      const map = new maps.Map(containerRef.current, {
        center: toSchoolTrace[0] || rideHomeTrace[0],
        zoom: 15,
        mapTypeControl: false,
        streetViewControl: false,
        gestureHandling: 'greedy',
      })
      mapRef.current = map
      const bounds = new maps.LatLngBounds()
      drawTrace(maps, map, bounds, toSchoolTrace, '#2563eb', 'School')
      drawTrace(maps, map, bounds, rideHomeTrace, '#ea580c', 'Home')
      if (!bounds.isEmpty()) map.fitBounds(bounds, 36)
      window.setTimeout(() => {
        if (!cancelled) maps.event.trigger(map, 'resize')
      }, 150)
    }).catch(() => {})
    return () => {
      cancelled = true
      mapRef.current = null
    }
  }, [traceKey, toSchoolTrace, rideHomeTrace])

  if (toSchoolTrace.length + rideHomeTrace.length === 0) {
    return (
      <div style={{
        height, display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'var(--bg-muted, #f8fafc)', color: 'var(--text-muted)', fontSize: 13, borderRadius: 8
      }}>
        No GPS path stored for this visit
      </div>
    )
  }

  return <div ref={containerRef} style={{ height, width: '100%', borderRadius: 8, overflow: 'hidden' }} />
}

function TravelledPathMap({ visits }) {
  const containerRef = useRef(null)
  const legs = useMemo(() => (
    (visits || []).flatMap((visit) => ([
      { color: '#2563eb', points: tracePoints(visit.path_to_school), endTitle: 'School' },
      { color: '#ea580c', points: tracePoints(visit.path_ride_home), endTitle: 'Home' },
    ])).filter((leg) => leg.points.length > 0)
  ), [visits])
  const legsKey = useMemo(() => JSON.stringify(legs), [legs])

  useEffect(() => {
    if (!containerRef.current || legs.length === 0) return undefined
    let cancelled = false
    loadGoogleMaps().then((maps) => {
      if (cancelled || !containerRef.current) return
      const map = new maps.Map(containerRef.current, {
        center: legs[0].points[0],
        zoom: 13,
        mapTypeControl: false,
        streetViewControl: false,
        gestureHandling: 'greedy',
      })
      const bounds = new maps.LatLngBounds()
      legs.forEach((leg) => drawTrace(maps, map, bounds, leg.points, leg.color, leg.endTitle, false))
      if (!bounds.isEmpty()) map.fitBounds(bounds, 36)
      window.setTimeout(() => {
        if (!cancelled) maps.event.trigger(map, 'resize')
      }, 150)
    }).catch(() => {})
    return () => { cancelled = true }
    // legsKey is the serialized trace, so a new visits array with the same points does not redraw the map.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legsKey])

  if (legs.length === 0) return null

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 8, fontSize: 12, color: 'var(--text-muted)' }}>
        <span><span style={{ color: '#2563eb', fontWeight: 700 }}>━</span> Recorded ride to school</span>
        <span><span style={{ color: '#ea580c', fontWeight: 700 }}>━</span> Recorded ride home</span>
      </div>
      <div ref={containerRef} style={{ height: 360, width: '100%', borderRadius: 8, overflow: 'hidden' }} />
    </div>
  )
}

function PersonDetails({ detail, expandedVisitId, setExpandedVisitId, emptyVisitsMessage }) {
  if (detail?.loading) {
    return <div className="loading"><div className="spinner" /><span>Loading details...</span></div>
  }
  if (detail?.error) {
    return <div className="alert alert-error">{detail.error}</div>
  }

  const monthWise = detail?.monthWise || []
  const visits = (detail?.visits || []).filter((visit) => (
    visit.visited_date || visit.status === 'visited' || visit.status === 'riding_home'
  ))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {monthWise.length > 0 && (
        <div>
          <h3 className="card-title" style={{ marginBottom: 10 }}>
            <Calendar size={16} />
            Month wise
          </h3>
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Visits</th>
                  <th>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Bike size={13} /> Bike travel
                    </span>
                  </th>
                  <th>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <Bus size={13} /> Bus charge
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {monthWise.map((row) => (
                  <tr key={row.month || row.month_label}>
                    <td style={{ fontWeight: 600 }}>{row.month_label}</td>
                    <td>{row.visit_count}</td>
                    <td><span className="chip chip-slate">{formatKm(row.bike_km)}</span></td>
                    <td><span className="chip chip-green">{formatMoney(row.bus_charge)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <h3 className="card-title" style={{ marginBottom: 10 }}>
          <Route size={16} />
          Travelled paths
        </h3>
        <TravelledPathMap visits={visits} />
        {visits.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>
            {emptyVisitsMessage}
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Planned date</th>
                  <th>Visited date</th>
                  <th>School</th>
                  <th>Home to location / to school</th>
                  <th>Location to home</th>
                  <th>Path</th>
                </tr>
              </thead>
              <tbody>
                {visits.map((visit) => {
                  const expanded = expandedVisitId === visit.id
                  const toSchoolLabel = visit.transport_mode === 'bus'
                    ? formatMoney(visit.bus_charge)
                    : formatKm(visit.distance_km)
                  const homeLabel = visit.home_end_time
                    ? (visit.home_transport_mode === 'bus'
                      ? formatMoney(visit.home_bus_charge)
                      : formatKm(visit.home_distance_km))
                    : '—'
                  return (
                    <Fragment key={visit.id}>
                      <tr>
                        <td style={{ whiteSpace: 'nowrap' }}>{visit.planned_date || '—'}</td>
                        <td style={{ whiteSpace: 'nowrap' }}>{visit.visited_date || '—'}</td>
                        <td style={{ fontWeight: 600 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <MapPin size={13} />
                            {visit.area_name === 'From Home' ? 'Home commute' : schoolName(visit.area_name)}
                          </span>
                        </td>
                        <td>
                          <span className="chip chip-slate">{toSchoolLabel}</span>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                            {visit.area_name === 'From Home' ? 'Home to location' : 'To school'}
                            {visit.transport_mode ? ` · ${visit.transport_mode}` : ''}
                          </div>
                        </td>
                        <td>
                          <span className="chip chip-slate">{homeLabel}</span>
                          {(visit.area_name === 'From Home' || visit.home_end_time) && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                              Location to home
                              {visit.home_transport_mode ? ` · ${visit.home_transport_mode}` : ''}
                            </div>
                          )}
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            onClick={() => setExpandedVisitId(expanded ? null : visit.id)}
                          >
                            {expanded ? 'Hide path' : 'Show path'}
                          </button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={6} style={{ background: 'var(--bg-muted, #f8fafc)', padding: 16 }}>
                            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 10, fontSize: 12, color: 'var(--text-muted)' }}>
                              <span><span style={{ color: '#2563eb', fontWeight: 700 }}>━</span> Recorded ride to school ({tracePoints(visit.path_to_school).length} pts)</span>
                              <span><span style={{ color: '#ea580c', fontWeight: 700 }}>━</span> Recorded ride home ({tracePoints(visit.path_ride_home).length} pts)</span>
                            </div>
                            <PathMap
                              toSchool={visit.path_to_school || []}
                              rideHome={visit.path_ride_home || []}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

export default function PersonsPage() {
  const [year, setYear] = useState(() => String(new Date().getFullYear()))
  const [dateFrom, setDateFrom] = useState(localToday)
  const [dateTo, setDateTo] = useState(localToday)
  const [people, setPeople] = useState([])
  const [expandedPersonId, setExpandedPersonId] = useState(null)
  const [expandedVisitId, setExpandedVisitId] = useState(null)
  const [details, setDetails] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [groups, setGroups] = useState([])
  const [groupId, setGroupId] = useState('')
  const [groupsLoaded, setGroupsLoaded] = useState(false)
  const listGeneration = useRef(0)

  const yearOptions = useMemo(() => {
    const current = new Date().getFullYear()
    return Array.from({ length: 6 }, (_, i) => String(current - i))
  }, [])

  useEffect(() => {
    let mounted = true
    api.get('/users/groups/')
      .then(res => {
         if (!mounted) return
         let gList = []
         if (Array.isArray(res.data)) {
           gList = res.data
         } else if (res.data && Array.isArray(res.data.results)) {
           gList = res.data.results
         } else if (res.data && Array.isArray(res.data.data)) {
           gList = res.data.data
         } else if (res.data && Array.isArray(res.data.groups)) {
           gList = res.data.groups
         }
         setGroups(gList)
         const salesGroup = gList.find(g => g.name && g.name.toLowerCase() === 'sales')
         if (salesGroup) {
           setGroupId(String(salesGroup.id))
         }
      })
      .catch(err => console.error(err))
      .finally(() => {
         if (mounted) setGroupsLoaded(true)
      })
    return () => { mounted = false }
  }, [])

  useEffect(() => {
    if (!groupsLoaded) return
    let mounted = true
    const generation = listGeneration.current + 1
    listGeneration.current = generation
    const load = async () => {
      setLoading(true)
      setError('')
      setExpandedPersonId(null)
      setExpandedVisitId(null)
      setDetails({})
      try {
        const res = await api.get('/visits/expenses-summary/', {
          params: {
            ...(dateFrom || dateTo
              ? { date_from: dateFrom || dateTo, date_to: dateTo || dateFrom }
              : year ? { year } : {}),
            ...(groupId ? { group_id: groupId } : {}),
          },
        })
        if (!mounted) return
        setPeople(res.data.person_wise || [])
      } catch {
        if (mounted) setError('Failed to load sales persons expenses.')
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => { mounted = false }
  }, [year, dateFrom, dateTo, groupId, groupsLoaded])

  const loadPersonDetails = async (personId) => {
    const generation = listGeneration.current
    setDetails((prev) => ({
      ...prev,
      [personId]: { ...(prev[personId] || {}), loading: true, error: '' },
    }))
    try {
      const res = await api.get('/visits/expenses-summary/', {
        params: {
          person_id: personId,
          ...(dateFrom || dateTo
            ? { date_from: dateFrom || dateTo, date_to: dateTo || dateFrom }
            : year ? { year } : {}),
          ...(groupId ? { group_id: groupId } : {}),
        },
      })
      if (generation !== listGeneration.current) return
      setDetails((prev) => ({
        ...prev,
        [personId]: {
          loading: false,
          error: '',
          monthWise: res.data.month_wise || [],
          visits: res.data.visits || [],
        },
      }))
    } catch {
      if (generation !== listGeneration.current) return
      setDetails((prev) => ({
        ...prev,
        [personId]: {
          loading: false,
          error: 'Failed to load details.',
          monthWise: [],
          visits: [],
        },
      }))
    }
  }

  const togglePerson = (rowKey, personId) => {
    if (expandedPersonId === rowKey) {
      setExpandedPersonId(null)
      setExpandedVisitId(null)
      return
    }
    setExpandedPersonId(rowKey)
    setExpandedVisitId(null)
    if (personId && (!details[personId] || details[personId].error)) {
      loadPersonDetails(personId)
    }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Sales Persons Expenses</h1>
          <p className="page-subtitle">All staff, including people with no scheduled visits. Expand a row for the full details.</p>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div className="filter-toolbar" style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ margin: 0, minWidth: 120 }}>
            <label className="form-label">Group</label>
            <select
              className="form-control"
              value={groupId}
              onChange={(e) => setGroupId(e.target.value)}
            >
              <option value="">All Groups</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group" style={{ margin: 0, minWidth: 160 }}>
            <label className="form-label">From</label>
            <input
              type="date"
              className="form-control"
              value={dateFrom}
              max={dateTo || undefined}
              onChange={(e) => {
                const next = e.target.value
                setDateFrom(next)
                const nextYear = next.slice(0, 4)
                if (nextYear && yearOptions.includes(nextYear)) setYear(nextYear)
              }}
            />
          </div>
          <div className="form-group" style={{ margin: 0, minWidth: 160 }}>
            <label className="form-label">To</label>
            <input
              type="date"
              className="form-control"
              value={dateTo}
              min={dateFrom || undefined}
              onChange={(e) => {
                const next = e.target.value
                setDateTo(next)
                const nextYear = (dateFrom || next).slice(0, 4)
                if (nextYear && yearOptions.includes(nextYear)) setYear(nextYear)
              }}
            />
          </div>
          <div className="form-group" style={{ margin: 0, minWidth: 120 }}>
            <label className="form-label">Year</label>
            <select
              className="form-control"
              value={year}
              onChange={(e) => {
                setYear(e.target.value)
                setDateFrom('')
                setDateTo('')
              }}
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: 16 }}>{error}</div>}

      {loading ? (
        <div className="loading"><div className="spinner" /><span>Loading expenses...</span></div>
      ) : people.length === 0 ? (
        <div className="empty-state">
          <Users size={48} />
          <h3>No expenses found</h3>
          <p>{dateFrom || dateTo ? `No travel expenses for staff from ${dateFrom || dateTo} to ${dateTo || dateFrom}` : `No travel expenses for staff in ${year}`}</p>
        </div>
      ) : (
        <div className="card">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <IndianRupee size={13} /> Travel charges
                    </span>
                  </th>
                  <th>Visits</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {people.map((person) => {
                  const rowKey = person.person_id != null ? `person-${person.person_id}` : `user-${person.user_id}`
                  const expanded = expandedPersonId === rowKey
                  return (
                    <Fragment key={rowKey}>
                      <tr>
                        <td style={{ fontWeight: 600 }}>{person.person_name}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                            <span className="chip chip-slate">{formatKm(person.bike_km)}</span>
                            <span className="chip chip-green">{formatMoney(person.bus_charge)}</span>
                          </div>
                        </td>
                        <td>{person.visit_count}</td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            type="button"
                            className="btn btn-secondary btn-sm"
                            style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
                            onClick={() => togglePerson(rowKey, person.person_id)}
                          >
                            {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            {expanded ? 'Show less' : 'Expand more'}
                          </button>
                        </td>
                      </tr>
                      {expanded && (
                        <tr>
                          <td colSpan={4} style={{ background: 'var(--bg-muted, #f8fafc)', padding: 16 }}>
                            {person.designation ? (
                              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 12 }}>
                                {person.designation}
                              </div>
                            ) : null}
                            {person.person_id ? (
                              <PersonDetails
                                detail={details[person.person_id]}
                                expandedVisitId={expandedVisitId}
                                setExpandedVisitId={setExpandedVisitId}
                                emptyVisitsMessage={dateFrom || dateTo ? 'No visit paths for this date range.' : 'No visit paths for this year yet.'}
                              />
                            ) : (
                              <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>
                                No visits scheduled.
                              </div>
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}
